import { Avatar, Button, Box, Container, Typography, Grid } from "@mui/material"
import { styled } from "@mui/material/styles"
import { Navigate } from "react-router-dom"
import { EmailOutlined, PersonOutlined } from "@mui/icons-material"
import { useAuth } from "providers/AuthProvider"
import { useMessage } from "providers/MessageProvider"
import useForm from "hooks/useForm"
import TextInput from "inputs/TextInput"
import useQueryParam from "hooks/useQueryParam"
import { RequestAccount } from "tools/auth"
import { useState } from "react"
import InterfaceIdInput, { parseInterfaceIdFromURL as parseHeatPumpIdFromURL } from "inputs/InterfaceIdInput"
import { Copyright } from "components/Copyright"
import { useTranslation } from "react-i18next"
import FormLogo from "components/FormLogo"
import { ErrHeatPumpDoesNotExist } from "resources/backendErrors"

const StyledAvatar = styled(Avatar)(({ theme }) => ({
	margin: theme.spacing(1),
	backgroundColor: theme.palette.secondary.main,
}))

const StyledContent = styled("div")(({ theme }) => ({
	marginTop: theme.spacing(8),
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
}))

const StyledInfoBlock = styled("div")(({ theme }) => ({
	marginTop: theme.spacing(4),
	marginBottom: theme.spacing(2),
}))

const StyledForm = styled("form")(({ theme }) => ({
	width: "100%", // Fix IE 11 issue.
	marginTop: theme.spacing(1),
}))

const StyledSubmit = styled(Button)(({ theme }) => ({
	margin: theme.spacing(3, 0, 2),
}))

export default function CreateAccountPage() {
	const { t } = useTranslation(["accountCreation", "general"])
	const auth = useAuth()
	const message = useMessage()
	const [emailSentState, setEmailSentState] = useState<"start" | "sending" | "sent">("start")
	const redirect = useQueryParam("redirect")
	const heatPumpId = parseHeatPumpIdFromURL(redirect ?? "")
	const { register, submit } = useForm<RequestAccount>({ heatPumpId: heatPumpId ?? undefined }, handleSubmit)

	async function handleSubmit(input: RequestAccount) {
		try {
			setEmailSentState("sending")
			await auth.requestAccount(input)
			setEmailSentState("sent")
		} catch (e) {
			setEmailSentState("start")
			if (e instanceof Error) {
				if (e.message.includes(ErrHeatPumpDoesNotExist)) {
					message.error(t("Errors.InterfaceIDUnkown"))
					return
				}
				if (e instanceof Error && e.message.includes("An account with this email address already exists")) {
					message.error(t("Errors.AccountWithEmailAlreadyExists"))
					return
				}
			}
			message.error(t("Errors.WhileSendingEmail"))
		}
	}

	if (auth.user) {
		if (redirect) return <Navigate to={redirect} />
		return <Navigate to="/" />
	}

	if (emailSentState === "sent")
		return (
			<Container component="main" maxWidth="xs">
				<StyledContent>
					<FormLogo />
					<Grid container direction="column" spacing={5} alignItems="center" justifyContent="center">
						<Grid item>
							<StyledAvatar>
								<EmailOutlined />
							</StyledAvatar>
						</Grid>
						<Grid item>
							<Typography component="h1" variant="h5">
								{t("EmailSent")}
							</Typography>
						</Grid>
						<Grid item>
							<Typography>{t("InstructionsInMailbox")}</Typography>
						</Grid>
					</Grid>
				</StyledContent>
				<Box mt={8}>
					<Copyright />
				</Box>
			</Container>
		)

	return (
		<Container component="main" maxWidth="xs">
			<StyledContent>
				<FormLogo />
				<StyledAvatar>
					<PersonOutlined />
				</StyledAvatar>
				<Typography component="h1" variant="h5">
					{t("CreateAccount")}
				</Typography>
				<StyledInfoBlock>
					<Typography gutterBottom>{t("MsgInterfaceIDDirections")}</Typography>
					<Typography color="primary">{t("MsgIDSecurityWarning")}</Typography>
				</StyledInfoBlock>
				<StyledForm onSubmit={submit}>
					<InterfaceIdInput
						label={t("general:InterfaceID")}
						autoFocus={heatPumpId == null}
						disabled={emailSentState === "sending"}
						{...register("heatPumpId", { required: true })}
					/>
					<TextInput
						variant="outlined"
						label={t("general:EmailAdress")}
						autoComplete="email"
						type="email"
						autoFocus={heatPumpId != null}
						disabled={emailSentState === "sending"}
						{...register("email", { required: true, email: true })}
					/>
					<StyledSubmit
						formNoValidate
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						disabled={emailSentState === "sending"}
					>
						{t("general:SendEmail")}
					</StyledSubmit>
				</StyledForm>
			</StyledContent>
			<Box mt={8}>
				<Copyright />
			</Box>
		</Container>
	)
}
